import { Injectable } from "@angular/core";
import {
  AwaitingOstUploadEventFile,
  ExpiredOstUploadFile,
  OST_UPLOAD_BACKEND_DEADLINE_MS,
  OstUploadBatchRecord,
  OstUploadNameReservation,
  OstUploadUsageDirtyRecord,
  PendingOstUploadFile,
} from "../models/images/ost-upload-tracking.model";
import { IndexedDBDatabaseService } from "./indexed-db-database.service";
import { OST_CREATION_IMAGE_STORE, OST_UPLOAD_STORES } from "./indexed-db.schema";

export interface IOstUploadSnapshot {
  delivery: PendingOstUploadFile[];
  awaitingEvent: AwaitingOstUploadEventFile[];
  batches: OstUploadBatchRecord[];
  expired: ExpiredOstUploadFile[];
  usageDirty: OstUploadUsageDirtyRecord[];
}

interface IUploadLease {
  id: string;
  ownerTabId: string;
  expiresAt: number;
}

interface IImageCacheRecord {
  id: string;
  ownerId: string;
  ostNum: string;
  fileName: string;
  content: Blob;
  type: string;
  size: number;
  lastModified: number;
}

@Injectable({ providedIn: "root" })
export class OstUploadPersistenceService {
  constructor(private readonly database: IndexedDBDatabaseService) {}

  async saveOstUploadBatch(
    ownerId: string,
    ostNum: string | number,
    files: File[],
    confirmedFileNames: string[] = [],
    now = Date.now(),
    draftFileNames: string[] = files.map((file: File): string => file.name),
    batchId = this.newLocalId()
  ): Promise<PendingOstUploadFile[]> {
    if (files.length === 0) return [];

    const normalizedOstNum = String(ostNum);
    const db = await this.database.openDatabase();
    const transaction = db.transaction(
      [OST_CREATION_IMAGE_STORE, OST_UPLOAD_STORES.delivery, OST_UPLOAD_STORES.awaitingEvent, OST_UPLOAD_STORES.batch, OST_UPLOAD_STORES.reservation],
      "readwrite"
    );
    const completed = this.database.waitForTransaction(transaction);
    const draftStore = transaction.objectStore(OST_CREATION_IMAGE_STORE);
    const deliveryStore = transaction.objectStore(OST_UPLOAD_STORES.delivery);
    const awaitingEventStore = transaction.objectStore(OST_UPLOAD_STORES.awaitingEvent);
    const batchStore = transaction.objectStore(OST_UPLOAD_STORES.batch);
    const reservationStore = transaction.objectStore(OST_UPLOAD_STORES.reservation);

    try {
      const [existingBatch, existingDelivery, existingAwaiting, existingReservations] = await Promise.all([
        this.database.requestResult(batchStore.get(batchId) as IDBRequest<OstUploadBatchRecord | undefined>),
        this.database.requestResult(deliveryStore.getAll() as IDBRequest<PendingOstUploadFile[]>),
        this.database.requestResult(awaitingEventStore.getAll() as IDBRequest<AwaitingOstUploadEventFile[]>),
        this.database.requestResult(reservationStore.getAll() as IDBRequest<OstUploadNameReservation[]>),
      ]);
      if (existingBatch?.ownerId === ownerId && existingBatch.ostNum === normalizedOstNum) {
        [...new Set(draftFileNames)].forEach((fileName: string): void => {
          draftStore.delete(fileName);
        });
        await completed;
        return [];
      }
      const usedNames = new Set<string>(confirmedFileNames);
      [...existingDelivery, ...existingAwaiting]
        .filter((file: PendingOstUploadFile | AwaitingOstUploadEventFile): boolean => file.ownerId === ownerId && file.ostNum === normalizedOstNum)
        .forEach((file: PendingOstUploadFile | AwaitingOstUploadEventFile): void => {
          usedNames.add(file.fileName);
        });
      existingReservations
        .filter((reservation: OstUploadNameReservation): boolean => reservation.ownerId === ownerId && reservation.ostNum === normalizedOstNum)
        .forEach((reservation: OstUploadNameReservation): void => {
          usedNames.add(reservation.fileName);
        });

      const pendingFiles: PendingOstUploadFile[] = files.map((file: File): PendingOstUploadFile => {
        const fileName = this.getUniqueFileName(file.name, usedNames);
        usedNames.add(fileName);
        const id = this.getOstUploadFileId(ownerId, normalizedOstNum, fileName);
        return {
          id,
          ownerId,
          ostNum: normalizedOstNum,
          batchId,
          fileName,
          originalName: file.name,
          content: new Blob([file], { type: file.type }),
          type: file.type,
          size: file.size,
          lastModified: file.lastModified,
          attemptCount: 0,
          nextAttemptAt: now,
        };
      });
      const batch: OstUploadBatchRecord = {
        id: batchId,
        ownerId,
        ostNum: normalizedOstNum,
        fileIds: pendingFiles.map((file: PendingOstUploadFile): string => file.id),
        fileNames: pendingFiles.map((file: PendingOstUploadFile): string => file.fileName),
        acceptedFileIds: [],
        confirmedFileIds: [],
        createdAt: now,
      };

      batchStore.put(batch);
      pendingFiles.forEach((file: PendingOstUploadFile): void => {
        deliveryStore.put(file);
        reservationStore.put({
          id: this.getOstUploadFileId(ownerId, normalizedOstNum, file.fileName),
          ownerId,
          ostNum: normalizedOstNum,
          fileName: file.fileName,
        } satisfies OstUploadNameReservation);
      });
      [...new Set(draftFileNames)].forEach((fileName: string): void => {
        draftStore.delete(fileName);
      });
      await completed;
      return pendingFiles;
    } catch (error) {
      this.database.abortTransaction(transaction);
      throw error;
    }
  }

  async getOstUploadSnapshot(ownerId: string): Promise<IOstUploadSnapshot> {
    const db = await this.database.openDatabase();
    const transaction = db.transaction(
      [OST_UPLOAD_STORES.delivery, OST_UPLOAD_STORES.awaitingEvent, OST_UPLOAD_STORES.batch, OST_UPLOAD_STORES.expired, OST_UPLOAD_STORES.usageDirty],
      "readonly"
    );
    const [delivery, awaitingEvent, batches, expired, usageDirty] = await Promise.all([
      this.database.requestResult(transaction.objectStore(OST_UPLOAD_STORES.delivery).getAll() as IDBRequest<PendingOstUploadFile[]>),
      this.database.requestResult(transaction.objectStore(OST_UPLOAD_STORES.awaitingEvent).getAll() as IDBRequest<AwaitingOstUploadEventFile[]>),
      this.database.requestResult(transaction.objectStore(OST_UPLOAD_STORES.batch).getAll() as IDBRequest<OstUploadBatchRecord[]>),
      this.database.requestResult(transaction.objectStore(OST_UPLOAD_STORES.expired).getAll() as IDBRequest<ExpiredOstUploadFile[]>),
      this.database.requestResult(transaction.objectStore(OST_UPLOAD_STORES.usageDirty).getAll() as IDBRequest<OstUploadUsageDirtyRecord[]>),
    ]);

    return {
      delivery: delivery.filter((file: PendingOstUploadFile): boolean => file.ownerId === ownerId),
      awaitingEvent: awaitingEvent.filter((file: AwaitingOstUploadEventFile): boolean => file.ownerId === ownerId),
      batches: batches.filter((batch: OstUploadBatchRecord): boolean => batch.ownerId === ownerId),
      expired: expired.filter((file: ExpiredOstUploadFile): boolean => file.ownerId === ownerId),
      usageDirty: usageDirty.filter((record: OstUploadUsageDirtyRecord): boolean => record.ownerId === ownerId),
    };
  }

  async markOstUploadFailure(ownerId: string, id: string, errorMessage: string, nextAttemptAt: number): Promise<boolean> {
    const db = await this.database.openDatabase();
    const transaction = db.transaction([OST_UPLOAD_STORES.delivery], "readwrite");
    const completed = this.database.waitForTransaction(transaction);
    const store = transaction.objectStore(OST_UPLOAD_STORES.delivery);

    try {
      const file = await this.database.requestResult(store.get(id) as IDBRequest<PendingOstUploadFile | undefined>);
      if (file && file.ownerId === ownerId) {
        store.put({ ...file, attemptCount: file.attemptCount + 1, nextAttemptAt, lastError: errorMessage });
      }
      await completed;
      return !!file && file.ownerId === ownerId;
    } catch (error) {
      this.database.abortTransaction(transaction);
      throw error;
    }
  }

  async acceptOstUploadFile(ownerId: string, id: string, now = Date.now(), deadlineMs = OST_UPLOAD_BACKEND_DEADLINE_MS): Promise<boolean> {
    const db = await this.database.openDatabase();
    const transaction = db.transaction([OST_UPLOAD_STORES.delivery, OST_UPLOAD_STORES.awaitingEvent, OST_UPLOAD_STORES.batch], "readwrite");
    const completed = this.database.waitForTransaction(transaction);
    const deliveryStore = transaction.objectStore(OST_UPLOAD_STORES.delivery);
    const awaitingStore = transaction.objectStore(OST_UPLOAD_STORES.awaitingEvent);
    const batchStore = transaction.objectStore(OST_UPLOAD_STORES.batch);

    try {
      const delivery = await this.database.requestResult(deliveryStore.get(id) as IDBRequest<PendingOstUploadFile | undefined>);
      if (!delivery || delivery.ownerId !== ownerId) {
        await completed;
        return false;
      }
      const [batch, existingAwaiting] = await Promise.all([
        this.database.requestResult(batchStore.get(delivery.batchId) as IDBRequest<OstUploadBatchRecord | undefined>),
        this.database.requestResult(awaitingStore.getAll() as IDBRequest<AwaitingOstUploadEventFile[]>),
      ]);
      if (!batch || batch.ownerId !== ownerId) {
        await completed;
        return false;
      }

      const acceptedFileIds = [...new Set([...batch.acceptedFileIds, delivery.id])];
      const updatedBatch: OstUploadBatchRecord = { ...batch, acceptedFileIds };
      const waitingFile: AwaitingOstUploadEventFile = {
        id: delivery.id,
        ownerId: delivery.ownerId,
        ostNum: delivery.ostNum,
        batchId: delivery.batchId,
        fileName: delivery.fileName,
        originalName: delivery.originalName,
        content: delivery.content,
        type: delivery.type,
        size: delivery.size,
        lastModified: delivery.lastModified,
      };

      deliveryStore.delete(id);
      const allAccepted = batch.fileIds.every((fileId: string): boolean => acceptedFileIds.includes(fileId) || batch.confirmedFileIds.includes(fileId));
      if (allAccepted && !batch.deadlineAt) {
        updatedBatch.allAcceptedAt = now;
        updatedBatch.deadlineAt = now + deadlineMs;
      }
      if (updatedBatch.deadlineAt) waitingFile.deadlineAt = updatedBatch.deadlineAt;
      awaitingStore.put(waitingFile);
      if (updatedBatch.deadlineAt) {
        existingAwaiting
          .filter((file: AwaitingOstUploadEventFile): boolean => file.batchId === batch.id && file.ownerId === ownerId)
          .forEach((file: AwaitingOstUploadEventFile): void => {
            awaitingStore.put({ ...file, deadlineAt: updatedBatch.deadlineAt });
          });
      }
      batchStore.put(updatedBatch);
      await completed;
      return true;
    } catch (error) {
      this.database.abortTransaction(transaction);
      throw error;
    }
  }

  async confirmOstUploadFile(ownerId: string, ostNum: string | number, fileName: string): Promise<{ confirmed: boolean; ostNum: string }> {
    const normalizedOstNum = String(ostNum);
    const id = this.getOstUploadFileId(ownerId, normalizedOstNum, fileName);
    const db = await this.database.openDatabase();
    const transaction = db.transaction(
      [OST_UPLOAD_STORES.delivery, OST_UPLOAD_STORES.awaitingEvent, OST_UPLOAD_STORES.batch, OST_UPLOAD_STORES.expired, OST_UPLOAD_STORES.usageDirty],
      "readwrite"
    );
    const completed = this.database.waitForTransaction(transaction);
    const deliveryStore = transaction.objectStore(OST_UPLOAD_STORES.delivery);
    const awaitingStore = transaction.objectStore(OST_UPLOAD_STORES.awaitingEvent);
    const batchStore = transaction.objectStore(OST_UPLOAD_STORES.batch);
    const expiredStore = transaction.objectStore(OST_UPLOAD_STORES.expired);
    const usageDirtyStore = transaction.objectStore(OST_UPLOAD_STORES.usageDirty);

    try {
      const [delivery, awaiting, expired] = await Promise.all([
        this.database.requestResult(deliveryStore.get(id) as IDBRequest<PendingOstUploadFile | undefined>),
        this.database.requestResult(awaitingStore.get(id) as IDBRequest<AwaitingOstUploadEventFile | undefined>),
        this.database.requestResult(expiredStore.get(id) as IDBRequest<ExpiredOstUploadFile | undefined>),
      ]);
      const record = delivery?.ownerId === ownerId ? delivery : awaiting?.ownerId === ownerId ? awaiting : undefined;
      const expiredRecord = expired?.ownerId === ownerId ? expired : undefined;
      if (!record && !expiredRecord) {
        await completed;
        return { confirmed: false, ostNum: normalizedOstNum };
      }

      const batchId = record?.batchId;
      let shouldRefreshUsage = false;
      if (delivery?.ownerId === ownerId) deliveryStore.delete(id);
      if (awaiting?.ownerId === ownerId) awaitingStore.delete(id);
      if (expiredRecord) expiredStore.delete(id);

      if (batchId) {
        const batch = await this.database.requestResult(batchStore.get(batchId) as IDBRequest<OstUploadBatchRecord | undefined>);
        if (batch?.ownerId === ownerId) {
          const confirmedFileIds = [...new Set([...batch.confirmedFileIds, id])];
          if (batch.fileIds.every((fileId: string): boolean => confirmedFileIds.includes(fileId))) {
            batchStore.delete(batchId);
            shouldRefreshUsage = true;
          } else {
            batchStore.put({ ...batch, confirmedFileIds });
          }
        } else {
          shouldRefreshUsage = true;
        }
      } else {
        shouldRefreshUsage = true;
      }
      if (shouldRefreshUsage) {
        usageDirtyStore.put({ id: this.getUsageDirtyId(ownerId, normalizedOstNum), ownerId, ostNum: normalizedOstNum } satisfies OstUploadUsageDirtyRecord);
      }
      await completed;
      return { confirmed: true, ostNum: normalizedOstNum };
    } catch (error) {
      this.database.abortTransaction(transaction);
      throw error;
    }
  }

  async expireOstUploadEvents(ownerId: string, now = Date.now()): Promise<number> {
    const db = await this.database.openDatabase();
    const transaction = db.transaction([OST_UPLOAD_STORES.awaitingEvent, OST_UPLOAD_STORES.batch, OST_UPLOAD_STORES.expired, OST_UPLOAD_STORES.usageDirty], "readwrite");
    const completed = this.database.waitForTransaction(transaction);
    const awaitingStore = transaction.objectStore(OST_UPLOAD_STORES.awaitingEvent);
    const batchStore = transaction.objectStore(OST_UPLOAD_STORES.batch);
    const expiredStore = transaction.objectStore(OST_UPLOAD_STORES.expired);

    try {
      const awaitingFiles = await this.database.requestResult(awaitingStore.getAll() as IDBRequest<AwaitingOstUploadEventFile[]>);
      const expiredFiles = awaitingFiles.filter((file: AwaitingOstUploadEventFile): boolean => file.ownerId === ownerId && file.deadlineAt !== undefined && file.deadlineAt <= now);
      const expiredOstNums = new Set<string>();
      expiredFiles.forEach((file: AwaitingOstUploadEventFile): void => {
        awaitingStore.delete(file.id);
        expiredOstNums.add(file.ostNum);
        expiredStore.put({ id: file.id, ownerId, ostNum: file.ostNum, fileName: file.fileName, expiredAt: now } satisfies ExpiredOstUploadFile);
        batchStore.delete(file.batchId);
      });
      const usageDirtyStore = transaction.objectStore(OST_UPLOAD_STORES.usageDirty);
      expiredOstNums.forEach((ostNum: string): void => {
        usageDirtyStore.put({ id: this.getUsageDirtyId(ownerId, ostNum), ownerId, ostNum } satisfies OstUploadUsageDirtyRecord);
      });
      await completed;
      return expiredFiles.length;
    } catch (error) {
      this.database.abortTransaction(transaction);
      throw error;
    }
  }

  async getDownloadedImageCache(ownerId: string, ostNum: string | number, fileName: string): Promise<Blob | undefined> {
    const normalizedOstNum = String(ostNum);
    const db = await this.database.openDatabase();
    const transaction = db.transaction([OST_UPLOAD_STORES.imageCache], "readonly");
    const record = await this.database.requestResult(
      transaction.objectStore(OST_UPLOAD_STORES.imageCache).get(this.getOstUploadFileId(ownerId, normalizedOstNum, fileName)) as IDBRequest<IImageCacheRecord | undefined>
    );
    return record?.ownerId === ownerId && record.ostNum === normalizedOstNum ? record.content : undefined;
  }

  async saveDownloadedImageCache(ownerId: string, ostNum: string | number, file: File): Promise<void> {
    const normalizedOstNum = String(ostNum);
    const db = await this.database.openDatabase();
    const transaction = db.transaction([OST_UPLOAD_STORES.imageCache], "readwrite");
    const completed = this.database.waitForTransaction(transaction);
    transaction.objectStore(OST_UPLOAD_STORES.imageCache).put({
      id: this.getOstUploadFileId(ownerId, normalizedOstNum, file.name),
      ownerId,
      ostNum: normalizedOstNum,
      fileName: file.name,
      content: new Blob([file], { type: file.type }),
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
    } satisfies IImageCacheRecord);
    await completed;
  }

  async setOstUploadUsageClean(ownerId: string, ostNum: string | number): Promise<void> {
    const db = await this.database.openDatabase();
    const transaction = db.transaction([OST_UPLOAD_STORES.usageDirty], "readwrite");
    const completed = this.database.waitForTransaction(transaction);
    transaction.objectStore(OST_UPLOAD_STORES.usageDirty).delete(this.getUsageDirtyId(ownerId, String(ostNum)));
    await completed;
  }

  async acquireOstUploadLease(id: string, ownerTabId: string, now = Date.now(), leaseMs = 45_000): Promise<boolean> {
    const db = await this.database.openDatabase();
    const transaction = db.transaction([OST_UPLOAD_STORES.lease], "readwrite");
    const completed = this.database.waitForTransaction(transaction);
    const store = transaction.objectStore(OST_UPLOAD_STORES.lease);

    try {
      const currentLease = await this.database.requestResult(store.get(id) as IDBRequest<IUploadLease | undefined>);
      const acquired = !currentLease || currentLease.expiresAt <= now || currentLease.ownerTabId === ownerTabId;
      if (acquired) store.put({ id, ownerTabId, expiresAt: now + leaseMs } satisfies IUploadLease);
      await completed;
      return acquired;
    } catch (error) {
      this.database.abortTransaction(transaction);
      throw error;
    }
  }

  async releaseOstUploadLease(id: string, ownerTabId: string): Promise<void> {
    const db = await this.database.openDatabase();
    const transaction = db.transaction([OST_UPLOAD_STORES.lease], "readwrite");
    const completed = this.database.waitForTransaction(transaction);
    const store = transaction.objectStore(OST_UPLOAD_STORES.lease);
    try {
      const currentLease = await this.database.requestResult(store.get(id) as IDBRequest<IUploadLease | undefined>);
      if (currentLease?.ownerTabId === ownerTabId) store.delete(id);
      await completed;
    } catch (error) {
      this.database.abortTransaction(transaction);
      throw error;
    }
  }

  private getOstUploadFileId(ownerId: string, ostNum: string, fileName: string): string {
    return JSON.stringify([ownerId, ostNum, fileName]);
  }

  private getUsageDirtyId(ownerId: string, ostNum: string): string {
    return JSON.stringify([ownerId, ostNum]);
  }

  private getUniqueFileName(originalName: string, usedNames: Set<string>): string {
    if (!usedNames.has(originalName)) return originalName;

    const extIndex = originalName.lastIndexOf(".");
    const extension = extIndex > 0 ? originalName.slice(extIndex) : "";
    const baseName = extIndex > 0 ? originalName.slice(0, extIndex) : originalName;
    let suffix = 1;
    let candidate = `${baseName}-${suffix}${extension}`;
    while (usedNames.has(candidate)) {
      suffix++;
      candidate = `${baseName}-${suffix}${extension}`;
    }
    return candidate;
  }

  private newLocalId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
